/**
 * Input schemas for every write in CPCel, grouped by area.
 *
 * These are the contract between a form and the service behind it. If a field
 * is not in the schema it does not reach the database — in particular
 * `organizationId` is never accepted from the client; it always comes from the
 * authenticated session.
 */
import { z } from 'zod';
import { CASH_DENOMINATIONS } from '@/lib/cash';
import {
  clientTxnIdSchema,
  dateKeySchema,
  emailSchema,
  idSchema,
  imeiSchema,
  monthKeySchema,
  moneySchema,
  optionalEmailSchema,
  optionalId,
  optionalImeiSchema,
  optionalMoneySchema,
  optionalPhoneSchema,
  paymentMethodSchema,
  phoneSchema,
  quantitySchema,
  requiredTextSchema,
  settlementMethodSchema,
  textSchema,
} from './common';

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export const loginSchema = z.object({
  /** Email, username, or mobile number — whichever the user finds easiest. */
  identifier: z.string().trim().min(1, 'Enter your email, username, or mobile number.').max(190),
  password: z.string().min(1, 'Enter your password.').max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({
  identifier: z.string().trim().min(1, 'Enter your email, username, or mobile number.').max(190),
});

export const resetPasswordSchema = z
  .object({
    token: z.string().trim().min(10, 'This reset link is not valid.'),
    password: z.string().min(8, 'Password must be at least 8 characters.').max(128),
    confirmPassword: z.string().min(1, 'Please re-enter the password.'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'The two passwords do not match.',
    path: ['confirmPassword'],
  });

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password.').max(128),
    password: z.string().min(8, 'Password must be at least 8 characters.').max(128),
    confirmPassword: z.string().min(1, 'Please re-enter the new password.'),
  })
  .refine((v) => v.password === v.confirmPassword, {
    message: 'The two passwords do not match.',
    path: ['confirmPassword'],
  });

/**
 * Identity fields carried by anything a device can create while offline.
 *
 * `clientTxnId` is the idempotency key, generated before the first send attempt.
 * `deviceId` says which handset it came from, which is what keeps one user's
 * queue on a shared device separate from the next user's. `occurredAt` is when
 * it really happened — not when it reached the server — and is what decides the
 * business day it belongs to.
 */
export const offlineIdentityFields = {
  clientTxnId: clientTxnIdSchema,
  deviceId: z.string().trim().min(8).max(64).optional(),
  occurredAt: z.string().datetime().optional(),
};

// ---------------------------------------------------------------------------
// Organisation & branches
// ---------------------------------------------------------------------------

export const organizationSchema = z.object({
  name: requiredTextSchema(120, 'Business name'),
  ownerName: requiredTextSchema(120, 'Owner name'),
  phone: phoneSchema,
  email: optionalEmailSchema,
  address: textSchema(240, 'Address').optional(),
  district: textSchema(60, 'District').optional(),
  currency: z.string().trim().length(3).default('BDT'),
  timezone: z.string().trim().min(1).default('Asia/Dhaka'),
});

export const branchSchema = z.object({
  name: requiredTextSchema(120, 'Customer Point name'),
  code: z
    .string()
    .trim()
    .min(2, 'Code must be at least 2 characters.')
    .max(6, 'Code must be 6 characters or fewer.')
    .regex(/^[A-Za-z0-9]+$/, 'Use letters and numbers only.')
    .transform((v) => v.toUpperCase()),
  gpPointCode: textSchema(40, 'GP Customer Point code').optional(),
  address: textSchema(240, 'Address').optional(),
  area: textSchema(80, 'Area').optional(),
  district: textSchema(60, 'District').optional(),
  phone: optionalPhoneSchema,
  openingDate: dateKeySchema.optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
});
export type BranchInput = z.infer<typeof branchSchema>;

// ---------------------------------------------------------------------------
// Users & agents
// ---------------------------------------------------------------------------

export const userSchema = z.object({
  name: requiredTextSchema(120, 'Name'),
  phone: phoneSchema,
  email: optionalEmailSchema,
  username: z
    .string()
    .trim()
    .toLowerCase()
    .min(3, 'Username must be at least 3 characters.')
    .max(40)
    .regex(/^[a-z0-9._-]+$/, 'Use letters, numbers, dot, dash, or underscore.')
    .optional()
    .or(z.literal(''))
    .transform((v) => (v ? v : undefined)),
  role: z.enum(['OWNER', 'MANAGER', 'AGENT'], { message: 'Select a role.' }),
  branchIds: z.array(idSchema).default([]),
  primaryBranchId: optionalId,
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
  joinDate: dateKeySchema.optional(),
  employeeCode: textSchema(40, 'Employee code').optional(),
  notes: textSchema(500, 'Notes').optional(),
});

export type UserInput = z.infer<typeof userSchema>;

/** Editing an existing person. Their password is changed by its own action. */
export const updateUserSchema = userSchema.extend({ userId: idSchema });
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export const createUserSchema = userSchema.extend({
  password: z.string().min(8, 'Password must be at least 8 characters.').max(128),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

/**
 * An agent's target for one period.
 *
 * Two measures, either or both. A GP counter selling recharge chases taka; one
 * pushing handsets is judged on units, because ten cheap phones and one
 * expensive one are not the same month's work. Zero on a measure means it is
 * not being tracked, which is why both are optional — but not both at once,
 * since a target of nothing is not a target.
 */
/**
 * One product's quantity target.
 *
 * A quantity of at least one, because a target of nothing is the absence of a
 * target rather than a target of zero — and a product listed at zero would sit on
 * the agent's sheet reading "0 of 0, 100% achieved", which is exactly the
 * confusion product-specific targets exist to remove. To stop measuring a
 * product, take it off the list.
 */
export const agentProductTargetSchema = z.object({
  productId: idSchema,
  quantity: quantitySchema({ min: 1, max: 1_000_000, label: 'Target quantity' }),
});
export type AgentProductTargetInput = z.infer<typeof agentProductTargetSchema>;

/**
 * An agent's target for one period.
 *
 * Two measures, either or both. A GP counter selling recharge chases taka; one
 * pushing handsets is judged on units of the models the shop needs moved, because
 * ten cheap phones and one expensive one are not the same month's work.
 *
 * The quantity side is a list of named products, never a blanket figure. A
 * blanket figure applies to everything in the catalogue, which lets an agent hit
 * it by selling whatever is easiest and gives every other product an implicit
 * target of zero. Products left off the list carry no target at all.
 *
 * Neither measure is required on its own; both empty is refused, since a target
 * of nothing is not a target.
 */
export const agentTargetSchema = z
  .object({
    agentProfileId: idSchema,
    period: z.enum(['DAILY', 'WEEKLY', 'MONTHLY'], { message: 'Choose daily, weekly, or monthly.' }),
    /**
     * `YYYY-MM-DD` for a daily target, the starting Saturday for a weekly one,
     * `YYYY-MM` for a monthly one.
     */
    periodKey: z.string().trim().min(7).max(10),
    /** Sales value to reach, in BDT. Zero or blank means no amount target. */
    targetAmount: optionalMoneySchema({ label: 'Sales amount target' }),
    /** The products carrying a quantity target. Empty means money only. */
    products: z.array(agentProductTargetSchema).max(100).default([]),
    notes: textSchema(240, 'Notes').optional(),
  })
  .refine((v) => Number(v.targetAmount) > 0 || v.products.length > 0, {
    message: 'Set a sales amount target, at least one product quantity, or both.',
    path: ['targetAmount'],
  })
  .refine((v) => new Set(v.products.map((entry) => entry.productId)).size === v.products.length, {
    message: 'Each product can carry only one target.',
    path: ['products'],
  });
export type AgentTargetInput = z.infer<typeof agentTargetSchema>;

// ---------------------------------------------------------------------------
// Catalogue
// ---------------------------------------------------------------------------

export const productSchema = z
  .object({
    name: requiredTextSchema(140, 'Product name'),
    code: z
      .string()
      .trim()
      .min(2, 'Product code must be at least 2 characters.')
      .max(40)
      .regex(/^[A-Za-z0-9._-]+$/, 'Use letters, numbers, dot, dash, or underscore.')
      .transform((v) => v.toUpperCase()),
    sku: textSchema(60, 'SKU').optional(),
    type: z.enum(['GP', 'HANDSET', 'OTHER'], { message: 'Select a product group.' }),
    kind: z.enum(['PHYSICAL', 'SERVICE']).default('PHYSICAL'),
    categoryId: optionalId,
    categoryName: textSchema(80, 'Category').optional(),
    purchaseCost: moneySchema({ label: 'Purchase cost' }),
    sellingPrice: moneySchema({ label: 'Selling price' }),
    /** The floor. Leave blank when the business does not set one. */
    minSellingPrice: optionalMoneySchema({ label: 'Minimum selling price' }).optional(),
    maxSellingPrice: optionalMoneySchema({ label: 'Maximum selling price' }).optional(),
    maxAgentDiscountPercent: z.coerce.number().min(0).max(100).optional(),
    maxManagerDiscountPercent: z.coerce.number().min(0).max(100).optional(),
    gpCommission: optionalMoneySchema({ label: 'GP commission' }),
    gpCommissionPercent: z.coerce.number().min(0).max(100).optional(),
    /** Selling this draws down a stored-value float instead of stock. */
    floatAccountId: optionalId,
    costingMethod: z.enum(['WEIGHTED_AVERAGE', 'SPECIFIC_IDENTIFICATION']).optional(),
    trackStock: z.boolean().default(true),
    imeiTracked: z.boolean().default(false),
    minStock: quantitySchema({ min: 0, label: 'Minimum stock' }).default(0),
    isFavourite: z.boolean().default(false),
    status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
    brand: textSchema(60, 'Brand').optional(),
    model: textSchema(80, 'Model').optional(),
    variant: textSchema(60, 'Variant').optional(),
    color: textSchema(40, 'Colour').optional(),
    warrantyInfo: textSchema(120, 'Warranty information').optional(),
    notes: textSchema(500, 'Notes').optional(),
  })
  .refine((v) => v.kind !== 'SERVICE' || !v.trackStock, {
    message: 'A service cannot track stock.',
    path: ['trackStock'],
  })
  .refine((v) => !v.imeiTracked || v.trackStock, {
    message: 'IMEI tracking requires stock tracking.',
    path: ['imeiTracked'],
  })
  .refine(
    (v) => v.minSellingPrice === undefined || Number(v.minSellingPrice) <= Number(v.sellingPrice),
    {
      message: 'The minimum price cannot be above the normal selling price.',
      path: ['minSellingPrice'],
    },
  )
  .refine(
    (v) => v.maxSellingPrice === undefined || Number(v.maxSellingPrice) >= Number(v.sellingPrice),
    {
      message: 'The maximum price cannot be below the normal selling price.',
      path: ['maxSellingPrice'],
    },
  )
  .refine(
    (v) =>
      v.maxAgentDiscountPercent === undefined ||
      v.maxManagerDiscountPercent === undefined ||
      v.maxAgentDiscountPercent <= v.maxManagerDiscountPercent,
    {
      // A manager who may give away less than their own agent is a rule nobody
      // meant to write.
      message: 'An agent cannot be allowed a larger discount than a manager.',
      path: ['maxAgentDiscountPercent'],
    },
  );
export type ProductInput = z.infer<typeof productSchema>;

// ---------------------------------------------------------------------------
// Suppliers & purchases
// ---------------------------------------------------------------------------

export const supplierSchema = z.object({
  name: requiredTextSchema(140, 'Supplier name'),
  contactPerson: textSchema(120, 'Contact person').optional(),
  phone: optionalPhoneSchema,
  email: optionalEmailSchema,
  address: textSchema(240, 'Address').optional(),
  notes: textSchema(500, 'Notes').optional(),
  openingBalance: optionalMoneySchema({ label: 'Opening balance' }),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
});
export type SupplierInput = z.infer<typeof supplierSchema>;

// Price controls live on the product, so they are edited where the price is.
export const purchaseItemSchema = z.object({
  productId: idSchema,
  quantity: quantitySchema({ label: 'Quantity' }),
  unitCost: moneySchema({ label: 'Unit cost' }),
  sellingPrice: moneySchema({ min: 0.01, label: 'Selling price' }).optional(),
  discount: optionalMoneySchema({ label: 'Discount' }),
  imeis: z.array(imeiSchema).default([]),
});

export const purchaseSchema = z
  .object({
    branchId: idSchema,
    supplierId: idSchema,
    invoiceNumber: textSchema(60, 'Invoice number').optional(),
    purchaseDate: dateKeySchema,
    items: z.array(purchaseItemSchema).min(1, 'Add at least one product to the purchase.'),
    discount: optionalMoneySchema({ label: 'Discount' }),
    paidAmount: optionalMoneySchema({ label: 'Amount paid' }),
    paymentMethod: settlementMethodSchema.optional(),
    notes: textSchema(500, 'Notes').optional(),
    clientTxnId: clientTxnIdSchema,
  })
  .refine((v) => Number(v.paidAmount) === 0 || v.paymentMethod !== undefined, {
    message: 'Select how the payment was made.',
    path: ['paymentMethod'],
  });
export type PurchaseInput = z.infer<typeof purchaseSchema>;

export const supplierPaymentSchema = z.object({
  branchId: idSchema,
  supplierId: idSchema,
  amount: moneySchema({ min: 0.01, label: 'Payment amount' }),
  paymentMethod: settlementMethodSchema,
  reference: textSchema(60, 'Reference').optional(),
  notes: textSchema(500, 'Notes').optional(),
  paymentDate: dateKeySchema,
  /** Set when the manager knowingly pays more than the recorded balance. */
  allowOverpayment: z.boolean().default(false),
  clientTxnId: clientTxnIdSchema,
});
export type SupplierPaymentInput = z.infer<typeof supplierPaymentSchema>;

// ---------------------------------------------------------------------------
// Customers & sales
// ---------------------------------------------------------------------------

export const customerSchema = z.object({
  name: requiredTextSchema(140, 'Customer name'),
  phone: phoneSchema,
  address: textSchema(240, 'Address').optional(),
  notes: textSchema(500, 'Notes').optional(),
  creditLimit: optionalMoneySchema({ label: 'Credit limit' }).optional(),
});
export type CustomerInput = z.infer<typeof customerSchema>;

/**
 * Permission to sell a line outside the price rules.
 *
 * Carried on the sale rather than requested separately, because the decision is
 * made at the counter in the same breath as the sale. What makes it a control is
 * that it is recorded, attributed and countable — not that it is hard to do.
 */
export const priceOverrideSchema = z.object({
  /** Index of the sale line it applies to. */
  itemIndex: z.coerce.number().int().min(0).max(199),
  note: requiredTextSchema(240, 'Reason'),
});
export type PriceOverrideInput = z.infer<typeof priceOverrideSchema>;

export const saleItemSchema = z.object({
  productId: idSchema,
  quantity: quantitySchema({ label: 'Quantity' }),
  unitPrice: moneySchema({ label: 'Price' }),
  discount: optionalMoneySchema({ label: 'Discount' }),
  imei: optionalImeiSchema,
});

export const salePaymentSchema = z.object({
  method: settlementMethodSchema,
  amount: moneySchema({ min: 0.01, label: 'Payment amount' }),
  reference: textSchema(60, 'Reference').optional(),
});

export const saleSchema = z
  .object({
    branchId: idSchema,
    /** Owners and managers may sell on behalf of an agent; an agent may not. */
    agentProfileId: optionalId,
    items: z.array(saleItemSchema).min(1, 'Add at least one product to the sale.'),
    discount: optionalMoneySchema({ label: 'Discount' }),
    payments: z.array(salePaymentSchema).default([]),
    customerId: optionalId,
    /** Inline customer capture, so a credit sale never needs a second screen. */
    customerName: textSchema(140, 'Customer name').optional(),
    customerPhone: optionalPhoneSchema,
    customerAddress: textSchema(240, 'Address').optional(),
    dueDate: dateKeySchema.optional(),
    notes: textSchema(500, 'Notes').optional(),
    saleDate: dateKeySchema.optional(),
    /**
     * Lines the seller is knowingly pricing outside the rules, each with a
     * reason. A line that needs one and does not have one is refused.
     */
    priceOverrides: z.array(priceOverrideSchema).max(50).default([]),
    /** Store credit applied to this sale, where the business offers it. */
    storeCreditApplied: optionalMoneySchema({ label: 'Store credit' }),
    ...offlineIdentityFields,
  })
  .refine((v) => v.items.every((i) => Number(i.discount ?? 0) <= Number(i.unitPrice) * i.quantity), {
    message: 'A line discount cannot be more than the line total.',
    path: ['items'],
  });
export type SaleInput = z.infer<typeof saleSchema>;

export const customerPaymentSchema = z.object({
  branchId: idSchema,
  customerId: idSchema,
  amount: moneySchema({ min: 0.01, label: 'Amount received' }),
  method: settlementMethodSchema,
  reference: textSchema(60, 'Reference').optional(),
  notes: textSchema(500, 'Notes').optional(),
  paymentDate: dateKeySchema,
  ...offlineIdentityFields,
});
export type CustomerPaymentInput = z.infer<typeof customerPaymentSchema>;

// ---------------------------------------------------------------------------
// Returns & adjustments
// ---------------------------------------------------------------------------

export const returnDispositionSchema = z.enum(
  ['RETURN_TO_STOCK', 'DAMAGED', 'SUPPLIER_RETURN', 'DO_NOT_RESTOCK'],
  { message: 'Choose what happens to the returned item.' },
);

export const returnItemSchema = z.object({
  saleItemId: optionalId,
  productId: idSchema,
  quantity: quantitySchema({ label: 'Quantity' }),
  unitPrice: moneySchema({ label: 'Unit price' }),
  /**
   * What physically happens to the item. `restock` is kept for continuity and
   * derived from this, because a boolean cannot tell damaged stock apart from
   * stock going back to the supplier.
   */
  disposition: returnDispositionSchema.default('RETURN_TO_STOCK'),
  restock: z.boolean().optional(),
  /** Why this line came back. A torn box and a faulty screen are not the same. */
  reason: textSchema(240, 'Reason').optional(),
  /** The handset coming back, for IMEI-tracked stock. */
  imei: optionalImeiSchema,
  replacementProductId: optionalId,
});

export const saleReturnSchema = z
  .object({
    branchId: idSchema,
    saleId: optionalId,
    customerId: optionalId,
    type: z.enum(['REFUND', 'EXCHANGE']).default('REFUND'),
    reason: requiredTextSchema(240, 'Reason'),
    items: z.array(returnItemSchema).min(1, 'Add at least one product to the return.'),
    refundAmount: optionalMoneySchema({ label: 'Refund amount' }),
    refundMethod: settlementMethodSchema.optional(),
    /**
     * Refunded as credit against a future purchase rather than as money.
     * Only available when the business has switched store credit on.
     */
    storeCreditAmount: optionalMoneySchema({ label: 'Store credit' }).optional(),
    approvalNote: textSchema(240, 'Approval note').optional(),
    returnDate: dateKeySchema,
    clientTxnId: clientTxnIdSchema,
  })
  .refine(
    (v) =>
      Number(v.storeCreditAmount ?? 0) === 0 ||
      Number(v.refundAmount ?? 0) === 0 ||
      v.refundMethod !== undefined,
    {
      message: 'Choose how the money part of the refund is being paid.',
      path: ['refundMethod'],
    },
  )
  .refine((v) => Number(v.storeCreditAmount ?? 0) === 0 || v.customerId !== undefined, {
    // Credit with nobody to owe it to is a liability that can never be redeemed.
    message: 'Store credit needs a named customer.',
    path: ['customerId'],
  });
export type SaleReturnInput = z.infer<typeof saleReturnSchema>;

export const inventoryAdjustmentSchema = z.object({
  branchId: idSchema,
  productId: idSchema,
  type: z.enum(['DAMAGED', 'LOST', 'INTERNAL_USE', 'CORRECTION', 'OTHER'], {
    message: 'Select an adjustment type.',
  }),
  quantity: quantitySchema({ label: 'Quantity' }),
  /** `-1` removes stock (the usual case), `+1` adds it back. */
  direction: z.union([z.literal(1), z.literal(-1)]).default(-1),
  reason: requiredTextSchema(240, 'Reason'),
  adjustmentDate: dateKeySchema,
  clientTxnId: clientTxnIdSchema,
});
export type InventoryAdjustmentInput = z.infer<typeof inventoryAdjustmentSchema>;

// ---------------------------------------------------------------------------
// Day-end physical inventory
// ---------------------------------------------------------------------------

/**
 * One product's counted quantity.
 *
 * `quantity` is a count, so zero is a real and important answer: "I looked, and
 * there are none" is what turns a phantom balance into a variance somebody can
 * act on. `quantitySchema` starts at one, which is right for selling and wrong
 * here, hence the explicit `min: 0`.
 */
export const stockCountLineSchema = z.object({
  productId: idSchema,
  quantity: quantitySchema({ min: 0, max: 1_000_000, label: 'Counted quantity' }),
  /** Required by the server whenever this line turns out to have a variance. */
  note: textSchema(240, 'Reason').optional(),
});
export type StockCountLineInput = z.infer<typeof stockCountLineSchema>;

/**
 * Entering counts for a day, at one of its two ends.
 *
 * One shape for both phases rather than two nearly identical ones: the fields
 * are the same, only the column they land in differs, and a single code path is
 * one fewer place for the opening and the closing to drift apart.
 *
 * Lines are partial by design. A shop counting its handsets this evening and
 * its cables tomorrow sends what it counted; a product left out is recorded as
 * not counted, which is honest, and is not the same as counted-and-matching.
 */
export const stockCountEntrySchema = z.object({
  branchId: idSchema,
  businessDate: dateKeySchema,
  phase: z.enum(['OPENING', 'CLOSING'], { message: 'Choose the opening or the closing count.' }),
  lines: z.array(stockCountLineSchema).min(1, 'Enter at least one counted quantity.').max(2000),
  notes: textSchema(500, 'Notes').optional(),
});
export type StockCountEntryInput = z.infer<typeof stockCountEntrySchema>;

/**
 * Signing the day off.
 *
 * `notes` is the sheet's own note. There is deliberately no blanket
 * "explanation" field that would let one sentence stand in for every variance
 * on the sheet: a reason belongs to the line it explains, and the server
 * refuses to finalise while any line with a gap is missing one.
 */
export const stockCountFinalizeSchema = z.object({
  branchId: idSchema,
  businessDate: dateKeySchema,
  notes: textSchema(500, 'Notes').optional(),
});
export type StockCountFinalizeInput = z.infer<typeof stockCountFinalizeSchema>;

/**
 * Correcting one line of a count that has already been signed off.
 *
 * The reason is mandatory and long enough to be a sentence, because this is the
 * one route by which a finalised count can change and the audit trail is the
 * whole point of having a route at all.
 */
export const stockCountAmendSchema = z.object({
  branchId: idSchema,
  businessDate: dateKeySchema,
  productId: idSchema,
  quantity: quantitySchema({ min: 0, max: 1_000_000, label: 'Corrected quantity' }),
  reason: requiredTextSchema(500, 'Reason'),
});
export type StockCountAmendInput = z.infer<typeof stockCountAmendSchema>;


// ---------------------------------------------------------------------------
// Expenses, commission, withdrawals, salary
// ---------------------------------------------------------------------------

export const expenseSchema = z.object({
  branchId: idSchema,
  categoryId: optionalId,
  categoryName: textSchema(80, 'Category').optional(),
  amount: moneySchema({ min: 0.01, label: 'Amount' }),
  method: settlementMethodSchema,
  description: textSchema(240, 'Description').optional(),
  expenseDate: dateKeySchema,
  ...offlineIdentityFields,
});
export type ExpenseInput = z.infer<typeof expenseSchema>;

export const expenseCategorySchema = z.object({
  name: requiredTextSchema(80, 'Category name'),
});

export const gpCommissionSchema = z.object({
  branchId: idSchema,
  productId: optionalId,
  type: z.enum(['PRODUCT', 'SERVICE', 'MANUAL']).default('MANUAL'),
  description: requiredTextSchema(200, 'Description'),
  rate: optionalMoneySchema({ label: 'Rate' }).optional(),
  quantity: quantitySchema({ min: 0, label: 'Quantity' }).optional(),
  amount: moneySchema({ min: 0.01, label: 'Commission amount' }),
  periodKey: monthKeySchema.optional(),
  notes: textSchema(500, 'Notes').optional(),
  commissionDate: dateKeySchema,
  clientTxnId: clientTxnIdSchema,
});
export type GpCommissionInput = z.infer<typeof gpCommissionSchema>;

export const ownerWithdrawalSchema = z.object({
  branchId: idSchema,
  ownerName: requiredTextSchema(120, 'Owner name'),
  amount: moneySchema({ min: 0.01, label: 'Amount' }),
  source: settlementMethodSchema.default('CASH'),
  notes: textSchema(500, 'Notes').optional(),
  withdrawalDate: dateKeySchema,
  clientTxnId: clientTxnIdSchema,
});
export type OwnerWithdrawalInput = z.infer<typeof ownerWithdrawalSchema>;

/**
 * Somebody on the payroll at one Customer Point.
 *
 * Note what is not here: no password, no username, no role. An employee record
 * is a staff record, and creating one grants no access to CPCel. A person who
 * also needs to sign in gets a `User` as well, through `createUserSchema` and
 * `user:manage` — a separate form, a separate permission and a separate audit
 * entry, which is the point.
 *
 * Only the name and the Customer Point are required. A shop adding six people
 * on a Tuesday afternoon should not be stopped by a joining date nobody
 * remembers; the fields that are known get filled in, and the rest can follow.
 */
export const employeeSchema = z.object({
  branchId: idSchema,
  name: requiredTextSchema(120, 'Employee name'),
  designation: textSchema(80, 'Designation').optional(),
  phone: optionalPhoneSchema,
  // `.optional()` as well as "optional money": without the suffix the key has to
  // be *present* even to be empty, and a form that leaves the salary blank sends
  // no key at all. The same pairing as `customerSchema.creditLimit`.
  monthlySalary: optionalMoneySchema({ label: 'Monthly salary' }).optional(),
  /**
   * The components a payslip is built from.
   *
   * Optional, and zero by default: a shop that pays one flat figure should not
   * be made to split it three ways before it can record anybody. Payroll falls
   * back to `monthlySalary` as the basic when none of these is set.
   */
  basicSalary: optionalMoneySchema({ label: 'Basic salary' }).optional(),
  medicalAllowance: optionalMoneySchema({ label: 'Medical allowance' }).optional(),
  houseRentAllowance: optionalMoneySchema({ label: 'House rent allowance' }).optional(),
  /** The account they sell under, so their own KPI can be measured. */
  userId: optionalId,
  joinDate: dateKeySchema.optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
  notes: textSchema(500, 'Notes').optional(),
});
export type EmployeeInput = z.infer<typeof employeeSchema>;

/** Activating or deactivating one, which is its own action and its own audit entry. */
export const employeeStatusSchema = z.object({
  employeeId: idSchema,
  status: z.enum(['ACTIVE', 'INACTIVE']),
});
export type EmployeeStatusInput = z.infer<typeof employeeStatusSchema>;

export const salaryPaymentSchema = z.object({
  branchId: idSchema,
  employeeId: idSchema,
  periodKey: monthKeySchema,
  basicSalary: moneySchema({ label: 'Basic salary' }),
  bonus: optionalMoneySchema({ label: 'Bonus' }),
  advance: optionalMoneySchema({ label: 'Advance' }),
  deduction: optionalMoneySchema({ label: 'Deduction' }),
  paidAmount: moneySchema({ min: 0, label: 'Amount paid' }),
  method: settlementMethodSchema,
  notes: textSchema(500, 'Notes').optional(),
  paymentDate: dateKeySchema,
  clientTxnId: clientTxnIdSchema,
});
export type SalaryPaymentInput = z.infer<typeof salaryPaymentSchema>;

// ---------------------------------------------------------------------------
// KPI targets and payroll
// ---------------------------------------------------------------------------

export const kpiSourceSchema = z.enum(['GP', 'SUPPLIER', 'INTERNAL'], {
  message: 'Choose who set this target.',
});
export const kpiMetricSchema = z.enum(
  ['SALES_AMOUNT', 'PRODUCT_QUANTITY', 'RECHARGE_AMOUNT', 'COMMISSION_AMOUNT', 'TRANSACTION_COUNT'],
  { message: 'Choose what the target is measured in.' },
);
export const kpiIncentiveTypeSchema = z.enum(
  ['FIXED_ON_TARGET', 'PERCENT_OF_ACHIEVED', 'PER_UNIT', 'TIERED'],
  { message: 'Choose how reaching the target pays.' },
);

/**
 * One band of a tiered rule.
 *
 * `minPercent` can exceed 100 — GP pays more for beating a target than for
 * reaching it, and a band at 120% is the ordinary way that is expressed.
 */
export const kpiTierSchema = z.object({
  minPercent: z.coerce.number().min(0).max(1000),
  incentiveAmount: moneySchema({ label: 'Band amount' }),
});

export const kpiRuleSchema = z.object({
  /** Present when editing an existing rule. */
  id: optionalId,
  /** Omitted for a rule that applies to every Customer Point. */
  branchId: optionalId,
  name: requiredTextSchema(120, 'Target name'),
  source: kpiSourceSchema.default('GP'),
  supplierId: optionalId,
  metric: kpiMetricSchema,
  basis: z.enum(['EMPLOYEE', 'BRANCH']).default('EMPLOYEE'),
  productId: optionalId,
  productType: z.enum(['GP', 'HANDSET', 'OTHER']).optional(),
  targetValue: moneySchema({ min: 0.01, label: 'Target' }),
  incentiveType: kpiIncentiveTypeSchema.default('FIXED_ON_TARGET'),
  incentiveValue: optionalMoneySchema({ label: 'Incentive' }),
  maxIncentive: optionalMoneySchema({ label: 'Maximum incentive' }),
  effectiveFrom: monthKeySchema,
  effectiveTo: monthKeySchema.optional(),
  tiers: z.array(kpiTierSchema).max(12).default([]),
  notes: textSchema(500, 'Notes').optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
});
export type KpiRuleInput = z.infer<typeof kpiRuleSchema>;

/** A standing allowance or deduction on one employee. */
export const employeeAllowanceSchema = z.object({
  id: optionalId,
  employeeId: idSchema,
  label: requiredTextSchema(80, 'Label'),
  amount: moneySchema({ min: 0.01, label: 'Amount' }),
  isEarning: z.boolean().default(true),
  status: z.enum(['ACTIVE', 'INACTIVE']).default('ACTIVE'),
});
export type EmployeeAllowanceInput = z.infer<typeof employeeAllowanceSchema>;

/**
 * Drafting a month's payroll for one employee.
 *
 * The KPI figures are deliberately absent: they are computed from the
 * transactions, never sent from a form. What a person supplies is the part the
 * system cannot know — this month's advance, a deduction, a one-off bonus.
 */
export const payslipDraftSchema = z.object({
  employeeId: idSchema,
  periodKey: monthKeySchema,
  /** Override the employee's standing figures for this month only. */
  basicSalary: optionalMoneySchema({ label: 'Basic salary' }).optional(),
  medicalAllowance: optionalMoneySchema({ label: 'Medical allowance' }).optional(),
  houseRentAllowance: optionalMoneySchema({ label: 'House rent allowance' }).optional(),
  advance: optionalMoneySchema({ label: 'Advance' }),
  deduction: optionalMoneySchema({ label: 'Deduction' }),
  /** One-off earnings beyond the standing allowances, each with a name. */
  extraEarnings: z
    .array(
      z.object({
        label: requiredTextSchema(80, 'Label'),
        amount: moneySchema({ min: 0.01, label: 'Amount' }),
      }),
    )
    .max(20)
    .default([]),
  notes: textSchema(500, 'Notes').optional(),
});
export type PayslipDraftInput = z.infer<typeof payslipDraftSchema>;

export const payslipFinalizeSchema = z.object({
  payslipId: idSchema,
});
export type PayslipFinalizeInput = z.infer<typeof payslipFinalizeSchema>;

export const payslipPaySchema = z.object({
  payslipId: idSchema,
  paidAmount: moneySchema({ min: 0, label: 'Amount paid' }),
  method: settlementMethodSchema,
  paymentDate: dateKeySchema,
  notes: textSchema(500, 'Notes').optional(),
  clientTxnId: clientTxnIdSchema,
});
export type PayslipPayInput = z.infer<typeof payslipPaySchema>;

/**
 * Reopening a signed-off payslip.
 *
 * A reason is required and not optional-with-a-default: the whole purpose of
 * the workflow is that the record says why, and a blank reason would make the
 * trail as useless as silent editing.
 */
export const payslipReopenSchema = z.object({
  payslipId: idSchema,
  reason: requiredTextSchema(500, 'Reason'),
});
export type PayslipReopenInput = z.infer<typeof payslipReopenSchema>;

export const payslipCancelSchema = z.object({
  payslipId: idSchema,
  reason: requiredTextSchema(500, 'Reason'),
});
export type PayslipCancelInput = z.infer<typeof payslipCancelSchema>;

// ---------------------------------------------------------------------------
// Day open / close
// ---------------------------------------------------------------------------

/** One account's declared balance at opening or closing time. */
export const accountBalanceEntrySchema = z.object({
  accountId: idSchema,
  amount: moneySchema({ min: -99_999_999_999.99, label: 'Balance' }),
  note: textSchema(240, 'Note').optional(),
});
export type AccountBalanceEntryInput = z.infer<typeof accountBalanceEntrySchema>;

export const dailyOpeningSchema = z.object({
  branchId: idSchema,
  businessDate: dateKeySchema,
  openingCash: moneySchema({ min: 0, label: 'Opening cash' }),
  /**
   * Opening balance for each reconciled account. Optional so that a business
   * that only counts the drawer keeps working exactly as it did; when present,
   * the cash drawer entry takes precedence over `openingCash`.
   */
  accounts: z.array(accountBalanceEntrySchema).max(30).optional(),
  notes: textSchema(500, 'Notes').optional(),
});
export type DailyOpeningInput = z.infer<typeof dailyOpeningSchema>;

/**
 * One line of a cash count: how many of a given note or coin.
 *
 * The denominations are pinned to what Bangladesh actually circulates, and the
 * database carries the same list as a check constraint — a count for a ৳37
 * note is a typo, and a typo that reaches a drawer count is an evening spent
 * hunting a difference that was never there.
 */
export const denominationEntrySchema = z.object({
  denomination: z
    .number()
    .int()
    .refine((value) => CASH_DENOMINATIONS.includes(value as (typeof CASH_DENOMINATIONS)[number]), {
      message: 'That is not a note or coin in circulation.',
    }),
  /** Zero is meaningful: it records that a denomination was looked for. */
  quantity: z.number().int().min(0, 'A count cannot be negative.').max(100_000),
});
export type DenominationEntryInput = z.infer<typeof denominationEntrySchema>;

/** At most one line per denomination, so a count cannot contradict itself. */
export const denominationsSchema = z
  .array(denominationEntrySchema)
  .max(CASH_DENOMINATIONS.length)
  .refine(
    (entries) => new Set(entries.map((entry) => entry.denomination)).size === entries.length,
    { message: 'Each note or coin can only be counted once.' },
  );

export const dailyClosingSchema = z.object({
  branchId: idSchema,
  businessDate: dateKeySchema,
  actualCash: moneySchema({ min: 0, label: 'Actual cash' }),
  /**
   * The note-by-note breakdown of `actualCash`. Optional, because a Customer
   * Point that has not adopted the count should not be blocked from closing;
   * supplied, it must add up to `actualCash` exactly.
   */
  denominations: denominationsSchema.optional(),
  /**
   * The counted or checked balance of every reconciled account. Reconciling
   * only physical cash leaves the bKash and Nagad balances unverified, which is
   * where money quietly goes missing.
   */
  accounts: z.array(accountBalanceEntrySchema).max(30).optional(),
  notes: textSchema(500, 'Notes').optional(),
  /** Required when the gap is larger than the configured tolerance. */
  differenceNote: textSchema(500, 'Explanation').optional(),
});
export type DailyClosingInput = z.infer<typeof dailyClosingSchema>;

/**
 * Signing off a day that has already passed.
 *
 * Everything the ordinary closing takes, plus the one thing that makes it
 * accountable: why it is being done late. The reason has a floor of ten
 * characters rather than one, because the whole point of the field is that
 * somebody has to say something — and a mandatory field that accepts `.` is not
 * a control, it is a formality.
 */
export const lateDailyClosingSchema = dailyClosingSchema.extend({
  reason: requiredTextSchema(500, 'Reason for the late closing').refine(
    (value) => value.length >= 10,
    'Explain in a sentence why this day was not closed on the day itself.',
  ),
});
export type LateDailyClosingInput = z.infer<typeof lateDailyClosingSchema>;

/**
 * An agent closing their own till.
 *
 * Narrower than the Customer Point closing on purpose: an agent counts cash and
 * explains a gap. They do not reconcile bKash, Nagad or the bank — those are
 * the Customer Point's accounts, not theirs — and they never see the profit
 * figures that closing the day requires.
 */
export const agentClosingSchema = z.object({
  branchId: idSchema,
  businessDate: dateKeySchema,
  /** Omitted, an agent closes their own. A manager may name the agent. */
  agentId: idSchema.optional(),
  actualCash: moneySchema({ min: 0, label: 'Counted cash' }),
  denominations: denominationsSchema.optional(),
  notes: textSchema(500, 'Notes').optional(),
  differenceNote: textSchema(500, 'Explanation').optional(),
});
export type AgentClosingInput = z.infer<typeof agentClosingSchema>;

export const reopenDaySchema = z.object({
  branchId: idSchema,
  businessDate: dateKeySchema,
  reason: requiredTextSchema(240, 'Reason'),
});

// ---------------------------------------------------------------------------
// Corrections
// ---------------------------------------------------------------------------

export const correctionRequestSchema = z.object({
  saleId: idSchema,
  reason: requiredTextSchema(500, 'Reason'),
  requestedValues: z.object({
    items: z
      .array(
        z.object({
          saleItemId: idSchema,
          quantity: quantitySchema({ label: 'Quantity' }),
          unitPrice: moneySchema({ label: 'Price' }),
          discount: optionalMoneySchema({ label: 'Discount' }),
        }),
      )
      .min(1, 'Include at least one line to correct.'),
    discount: optionalMoneySchema({ label: 'Discount' }).optional(),
    notes: textSchema(500, 'Notes').optional(),
  }),
});
export type CorrectionRequestInput = z.infer<typeof correctionRequestSchema>;

/**
 * An agent asking for a signed-off day to be corrected.
 *
 * Three separate fields rather than one box, because a reviewer deciding in
 * thirty seconds needs to know three different things: what the agent says is
 * wrong, what it should be instead, and why they believe it. One paragraph
 * containing all three is what gets skimmed and waved through.
 */
export const closingCorrectionRequestSchema = z.object({
  branchId: idSchema,
  businessDate: dateKeySchema,
  reason: requiredTextSchema(500, 'Reason').refine(
    (value) => value.length >= 10,
    'Explain in a sentence why this closing is wrong.',
  ),
  incorrectDetail: requiredTextSchema(240, 'What is incorrect'),
  requestedCorrection: requiredTextSchema(240, 'What it should be'),
  /** Anything else worth knowing, including where the evidence is. */
  note: textSchema(500, 'Note').optional(),
});
export type ClosingCorrectionRequestInput = z.infer<typeof closingCorrectionRequestSchema>;

/**
 * The decision.
 *
 * `REQUEST_INFO` is a third outcome rather than a soft rejection. A reviewer who
 * needs more before deciding has not decided, and recording it as a rejection
 * would both lose the request and tell the agent something untrue.
 */
export const closingCorrectionReviewSchema = z
  .object({
    requestId: idSchema,
    decision: z.enum(['APPROVE', 'REJECT', 'REQUEST_INFO'], {
      message: 'Choose whether to approve, reject, or ask for more information.',
    }),
    reviewNote: textSchema(500, 'Note').optional(),
  })
  .refine((v) => v.decision === 'APPROVE' || Boolean(v.reviewNote?.trim()), {
    // Approving speaks for itself: the day reopens and the request says why.
    // Refusing, or sending it back, does not — and an agent told "no" with no
    // reason learns only that asking is pointless.
    message: 'Say why, so the agent knows what to do next.',
    path: ['reviewNote'],
  });
export type ClosingCorrectionReviewInput = z.infer<typeof closingCorrectionReviewSchema>;

/** Answering a reviewer who asked for more. */
export const closingCorrectionResubmitSchema = z.object({
  requestId: idSchema,
  note: requiredTextSchema(500, 'Answer').refine(
    (value) => value.length >= 5,
    'Add the detail your manager asked for.',
  ),
});
export type ClosingCorrectionResubmitInput = z.infer<typeof closingCorrectionResubmitSchema>;


export const correctionReviewSchema = z.object({
  correctionId: idSchema,
  decision: z.enum(['APPROVE', 'REJECT'], { message: 'Choose approve or reject.' }),
  reviewNote: textSchema(500, 'Note').optional(),
});

export const voidSaleSchema = z.object({
  saleId: idSchema,
  reason: requiredTextSchema(240, 'Reason'),
});

// ---------------------------------------------------------------------------
// Financial accounts
// ---------------------------------------------------------------------------

export const financialAccountTypeSchema = z.enum(
  [
    'CASH_DRAWER',
    'BKASH_MERCHANT',
    'BKASH_AGENT_FLOAT',
    'NAGAD_MERCHANT',
    'NAGAD_AGENT_FLOAT',
    'BANK_ACCOUNT',
    'CARD_CLEARING',
    'GP_RECHARGE_FLOAT',
    'OTHER_STORED_VALUE',
  ],
  { message: 'Choose what kind of account this is.' },
);

export const financialAccountSchema = z.object({
  /** Omitted for an account that belongs to the business rather than a branch. */
  branchId: optionalId,
  name: requiredTextSchema(80, 'Account name'),
  code: z
    .string()
    .trim()
    .min(2, 'Account code is required.')
    .max(24)
    .regex(/^[A-Za-z0-9-]+$/, 'Use letters, numbers and hyphens only.')
    .transform((v) => v.toUpperCase()),
  type: financialAccountTypeSchema,
  defaultForMethod: paymentMethodSchema.optional(),
  reconcileDaily: z.boolean().default(true),
  accountRef: textSchema(60, 'Account number').optional(),
  /**
   * ERS commission earned out of this float, as a percentage of the value
   * recharged. Only meaningful on a float account; blank means the
   * organisation's rate applies.
   */
  commissionPercent: z.union([z.string(), z.number(), z.null(), z.undefined()]).transform((value) => {
    if (value === undefined || value === null || String(value).trim() === '') return null;
    const parsed = Number(String(value).trim());
    return Number.isFinite(parsed) ? parsed : null;
  }).pipe(z.number().min(0, 'A rate cannot be negative.').max(100, 'A rate cannot be above 100%.').nullable()),
  notes: textSchema(500, 'Notes').optional(),
  sortOrder: z.coerce.number().int().min(0).max(999).optional(),
  openingBalance: optionalMoneySchema({ min: -99_999_999_999.99, label: 'Opening balance' }).optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']).optional(),
});
export type FinancialAccountInput = z.infer<typeof financialAccountSchema>;

export const accountTransferSchema = z
  .object({
    fromAccountId: idSchema,
    toAccountId: idSchema,
    amount: moneySchema({ min: 0.01, label: 'Transfer amount' }),
    businessDate: dateKeySchema,
    reference: textSchema(60, 'Reference').optional(),
    note: textSchema(500, 'Note').optional(),
    // A transfer moves real money between two accounts, so the same key that
    // stops a sale being written twice applies here. It matters for the case the
    // disabled submit button cannot cover: the request was sent, the reply never
    // arrived, and the person reloaded and recorded it again.
    clientTxnId: clientTxnIdSchema,
  })
  .refine((v) => v.fromAccountId !== v.toAccountId, {
    message: 'Choose two different accounts.',
    path: ['toAccountId'],
  });
export type AccountTransferInput = z.infer<typeof accountTransferSchema>;

export const voidTransferSchema = z.object({
  transferId: idSchema,
  reason: requiredTextSchema(240, 'Reason'),
});

export const accountAdjustmentSchema = z.object({
  accountId: idSchema,
  direction: z.enum(['IN', 'OUT'], { message: 'Choose whether money went in or out.' }),
  amount: moneySchema({ min: 0.01, label: 'Adjustment amount' }),
  businessDate: dateKeySchema,
  reason: requiredTextSchema(240, 'Reason'),
});
export type AccountAdjustmentInput = z.infer<typeof accountAdjustmentSchema>;

// ---------------------------------------------------------------------------
// Stored-value floats
// ---------------------------------------------------------------------------

export const floatMovementSchema = z.object({
  accountId: idSchema,
  amount: moneySchema({ min: 0.01, label: 'Amount' }),
  businessDate: dateKeySchema,
  /**
   * Which account paid for the float. Recording only the float side would make
   * the business look richer by the amount of every top-up.
   */
  fundedFromMethod: settlementMethodSchema.optional(),
  reference: textSchema(60, 'Reference').optional(),
  note: textSchema(500, 'Note').optional(),
});
export type FloatMovementInput = z.infer<typeof floatMovementSchema>;

// ---------------------------------------------------------------------------
// Commission settlement
// ---------------------------------------------------------------------------

export const commissionSettlementSchema = z
  .object({
    commissionId: idSchema,
    amount: moneySchema({ min: 0.01, label: 'Amount received' }),
    /** Where the money landed. Ignored for an adjustment, which moves none. */
    method: settlementMethodSchema.default('BANK'),
    /** True when GP disagreed and the receivable is being written down. */
    isAdjustment: z.boolean().default(false),
    statementReference: textSchema(60, 'Statement reference').optional(),
    note: textSchema(500, 'Note').optional(),
    settlementDate: dateKeySchema,
  })
  .refine((v) => !v.isAdjustment || (v.note?.trim().length ?? 0) > 0, {
    message: 'Explain why the commission is being adjusted.',
    path: ['note'],
  });
export type CommissionSettlementInput = z.infer<typeof commissionSettlementSchema>;

// ---------------------------------------------------------------------------
// Supplier returns
// ---------------------------------------------------------------------------

export const supplierReturnItemSchema = z.object({
  productId: idSchema,
  quantity: quantitySchema({ label: 'Quantity' }),
  imei: optionalImeiSchema,
  reason: textSchema(240, 'Reason').optional(),
});

export const supplierReturnSchema = z.object({
  branchId: idSchema,
  supplierId: idSchema,
  /** The invoice the goods came in on, so the credit can be reconciled. */
  purchaseId: optionalId,
  items: z.array(supplierReturnItemSchema).min(1, 'Add at least one item to send back.'),
  reason: requiredTextSchema(240, 'Reason'),
  creditNoteRef: textSchema(60, 'Credit note reference').optional(),
  notes: textSchema(500, 'Notes').optional(),
  returnDate: dateKeySchema,
});
export type SupplierReturnInput = z.infer<typeof supplierReturnSchema>;

export const voidSupplierReturnSchema = z.object({
  returnId: idSchema,
  reason: requiredTextSchema(240, 'Reason'),
});

// ---------------------------------------------------------------------------
// House returns and replacements
//
// A faulty unit going to the House for a replacement, and the replacement
// coming back. Two forms rather than one, because they are filled in days apart
// by whoever is on the counter when the courier arrives.
// ---------------------------------------------------------------------------

export const sendToHouseSchema = z.object({
  branchId: idSchema,
  /** The House or distributor the unit is going to. */
  supplierId: idSchema,
  productId: idSchema,
  quantity: quantitySchema({ label: 'Quantity' }),
  /** Required for IMEI-tracked stock: which handset went. */
  imei: optionalImeiSchema,
  serialNumber: textSchema(60, 'Serial number').optional(),
  /** The lifting invoice the faulty unit came in on, where it is known. */
  purchaseId: optionalId,
  /**
   * The customer-return line that found it faulty.
   *
   * Optional — faulty stock is also found on the shelf — but naming it is what
   * links the customer's complaint to the claim and stops the same faulty line
   * being claimed twice.
   */
  saleReturnItemId: optionalId,
  faultDescription: requiredTextSchema(240, 'What is wrong with it'),
  /** The challan or reference the unit travelled on. */
  challanRef: textSchema(60, 'Challan / reference').optional(),
  notes: textSchema(500, 'Notes').optional(),
  sendDate: dateKeySchema,
});
export type SendToHouseInput = z.infer<typeof sendToHouseSchema>;

export const receiveReplacementSchema = z
  .object({
    houseReturnId: idSchema,
    /** Usually the same product; a House substituting a model is ordinary. */
    replacementProductId: idSchema,
    replacementQuantity: quantitySchema({ label: 'Replacement quantity' }),
    replacementImei: optionalImeiSchema,
    replacementSerial: textSchema(60, 'Serial number').optional(),
    /**
     * What the replacement is worth. Left empty it is taken as like-for-like,
     * which is the common case and the one that settles at nothing.
     */
    replacementUnitCost: optionalMoneySchema({ label: 'Replacement cost' }),
    /** What the replacement will sell for. Defaults to the product's list price. */
    replacementSellingPrice: optionalMoneySchema({ label: 'Selling price' }),
    /**
     * How a difference is squared up. Ignored when there is none — the service
     * forces `NONE` on a like-for-like swap rather than trusting the screen.
     */
    settlement: z.enum(['ACCOUNT', 'CASH'], { message: 'Choose how the difference is settled.' }).default('ACCOUNT'),
    /** Which account the money moved through, for a cash settlement. */
    settlementMethod: settlementMethodSchema.default('CASH'),
    receiptRef: textSchema(60, 'Receipt / challan reference').optional(),
    notes: textSchema(500, 'Notes').optional(),
    receivedDate: dateKeySchema,
  });
export type ReceiveReplacementInput = z.infer<typeof receiveReplacementSchema>;

export const cancelHouseReturnSchema = z.object({
  houseReturnId: idSchema,
  reason: requiredTextSchema(240, 'Reason'),
  /**
   * True when the same unit came back unrepaired and goes back into the count.
   *
   * Only meaningful for a claim that took the unit out of the count in the first
   * place; the service refuses it for one that was already written off, rather
   * than quietly resurrecting written-off stock.
   */
  restoreToStock: z.boolean().default(false),
});
export type CancelHouseReturnInput = z.infer<typeof cancelHouseReturnSchema>;

// ---------------------------------------------------------------------------
// Price overrides
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Opening balances (onboarding)
// ---------------------------------------------------------------------------

export const openingBalanceAccountSchema = z.object({
  accountId: idSchema,
  amount: moneySchema({ min: -99_999_999_999.99, label: 'Balance' }),
});

export const openingBalanceCustomerSchema = z.object({
  customerId: optionalId,
  name: requiredTextSchema(140, 'Customer name'),
  phone: phoneSchema,
  amount: moneySchema({ min: 0.01, label: 'Amount owed' }),
  note: textSchema(240, 'Note').optional(),
});

export const openingBalanceSupplierSchema = z.object({
  supplierId: optionalId,
  name: requiredTextSchema(140, 'Supplier name'),
  amount: moneySchema({ min: 0.01, label: 'Amount owed' }),
  note: textSchema(240, 'Note').optional(),
});

export const openingBalanceStockSchema = z.object({
  productId: idSchema,
  quantity: quantitySchema({ label: 'Quantity' }),
  unitCost: moneySchema({ min: 0, label: 'Unit cost' }),
});

export const openingBalanceHandsetSchema = z.object({
  productId: idSchema,
  imei1: imeiSchema,
  imei2: optionalImeiSchema,
  serialNumber: textSchema(60, 'Serial number').optional(),
  purchaseCost: moneySchema({ min: 0, label: 'Purchase cost' }),
  sellingPrice: moneySchema({ min: 0, label: 'Selling price' }),
});

export const openingBalancesSchema = z.object({
  branchId: idSchema,
  /** The date the business's history is being carried in as at. */
  asOfDate: dateKeySchema,
  accounts: z.array(openingBalanceAccountSchema).max(40).default([]),
  customerDues: z.array(openingBalanceCustomerSchema).max(500).default([]),
  supplierDues: z.array(openingBalanceSupplierSchema).max(500).default([]),
  stock: z.array(openingBalanceStockSchema).max(2000).default([]),
  handsets: z.array(openingBalanceHandsetSchema).max(2000).default([]),
  note: textSchema(500, 'Note').optional(),
});
export type OpeningBalancesInput = z.infer<typeof openingBalancesSchema>;

// ---------------------------------------------------------------------------
// Store credit
// ---------------------------------------------------------------------------

export const redeemStoreCreditSchema = z.object({
  customerId: idSchema,
  branchId: idSchema,
  amount: moneySchema({ min: 0.01, label: 'Amount' }),
  saleId: optionalId,
  note: textSchema(240, 'Note').optional(),
  businessDate: dateKeySchema,
});
export type RedeemStoreCreditInput = z.infer<typeof redeemStoreCreditSchema>;

// ---------------------------------------------------------------------------
// Excel import
// ---------------------------------------------------------------------------

export const importKindSchema = z.enum(
  ['PRODUCTS', 'INVENTORY', 'HANDSETS', 'CUSTOMERS', 'CUSTOMER_DUE', 'SUPPLIERS', 'SUPPLIER_DUE'],
  { message: 'Choose what you are importing.' },
);

export const startImportSchema = z.object({
  kind: importKindSchema,
  branchId: optionalId,
  fileName: requiredTextSchema(200, 'File name'),
  /** The sheet, already parsed client-side into rows of strings. */
  rows: z.array(z.array(z.string().max(500))).min(2, 'The file needs a header row and at least one data row.').max(5001),
});
export type StartImportInput = z.infer<typeof startImportSchema>;

export const mapImportSchema = z.object({
  batchId: idSchema,
  /** CPCel field name → index of the source column that feeds it. */
  mapping: z.record(z.string().max(60), z.coerce.number().int().min(0).max(200)),
});
export type MapImportInput = z.infer<typeof mapImportSchema>;

export const commitImportSchema = z.object({
  batchId: idSchema,
  /** Import the valid rows and leave the invalid ones behind. */
  skipInvalid: z.boolean().default(true),
});
export type CommitImportInput = z.infer<typeof commitImportSchema>;

export const cancelImportSchema = z.object({ batchId: idSchema });

// ---------------------------------------------------------------------------
// Offline sync
// ---------------------------------------------------------------------------

export const syncBatchSchema = z.object({
  /**
   * The device replaying the queue. Stamped on every record so a manager can see
   * which handset still has work that has not reached the server, and so one
   * user's queue on a shared device is never attributed to the next.
   */
  deviceId: z.string().trim().min(8).max(64).optional(),
  operations: z
    .array(
      z.object({
        clientTxnId: z.string().uuid(),
        scope: z.enum(['sale', 'expense', 'customer_payment']),
        payload: z.unknown(),
        queuedAt: z.string().datetime().optional(),
        /**
         * When the transaction actually happened on the device. This — not the
         * moment it reached the server — decides which business day it belongs
         * to, which is the whole point of recording it.
         */
        occurredAt: z.string().datetime().optional(),
      }),
    )
    .min(1)
    .max(50, 'Too many queued transactions in one batch.'),
});
export type SyncBatchInput = z.infer<typeof syncBatchSchema>;

export const resolveConflictSchema = z.object({
  conflictId: idSchema,
  action: z.enum(['DISMISS', 'RETRY']),
  note: textSchema(500, 'Note').optional(),
});

// ---------------------------------------------------------------------------
// Subscription, support access and export (Phase 2)
// ---------------------------------------------------------------------------

export const subscriptionPayMethodSchema = z.enum([
  'BKASH',
  'NAGAD',
  'BANK_TRANSFER',
  'CASH',
  'CHEQUE',
  'CARD',
  'OTHER',
]);

export const reportSubscriptionPaymentSchema = z.object({
  invoiceId: idSchema,
  amount: moneySchema({ min: 0.01, label: 'Amount' }),
  method: subscriptionPayMethodSchema,
  /**
   * The bKash TrxID or bank reference. Not strictly required — a cash payment
   * handed over in person has none — but without it CPCel has nothing to match
   * against, so the form asks for it wherever one exists.
   */
  reference: textSchema(120, 'Transaction reference').optional(),
  paymentDate: dateKeySchema,
  notes: textSchema(500, 'Note').optional(),
});
export type ReportSubscriptionPaymentInput = z.infer<typeof reportSubscriptionPaymentSchema>;

export const grantSupportAccessSchema = z.object({
  /** Off unless the owner deliberately asks for it. */
  allowWrites: z.boolean().default(false),
  /** Hours, capped in the service. A grant without an end is not a grant. */
  hours: z.coerce.number().int().min(1).max(336).default(72),
  note: textSchema(240, 'Note').optional(),
});
export type GrantSupportAccessInput = z.infer<typeof grantSupportAccessSchema>;

export const revokeSupportAccessSchema = z.object({
  reason: textSchema(240, 'Reason').optional(),
});

export const dataExportSchema = z.object({
  datasets: z.array(z.string().max(60)).max(40).default([]),
  format: z.enum(['CSV', 'XLSX', 'JSON']).default('CSV'),
  fromDate: dateKeySchema.optional(),
  toDate: dateKeySchema.optional(),
});
export type DataExportInput = z.infer<typeof dataExportSchema>;

export const cancelSubscriptionSchema = z.object({
  reason: z.enum([
    'TOO_EXPENSIVE',
    'NOT_USING_ENOUGH',
    'MISSING_FEATURE',
    'DIFFICULT_TO_USE',
    'TECHNICAL_PROBLEMS',
    'BUSINESS_CLOSED',
    'SWITCHED_SOFTWARE',
    'SUPPORT_ISSUE',
    'TEMPORARY_PAUSE',
    'OTHER',
  ]),
  comment: textSchema(1000, 'Comment').optional(),
  requestExport: z.boolean().default(true),
});
export type CancelSubscriptionInput = z.infer<typeof cancelSubscriptionSchema>;

export const reactivateSubscriptionSchema = z.object({
  note: textSchema(240, 'Note').optional(),
});

export { paymentMethodSchema, settlementMethodSchema, emailSchema, dateKeySchema, monthKeySchema };
